import React, { useCallback, useContext, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { useControlledState } from 'ui-components/hooks/use-controlled-component-state/use-controlled-component-state';

import { createColumnDefinitionBuilder } from '../../../../../../../components/items-table/create-column-definition-builder';
import { ProductDetailsContext } from '../../../../../context';

import { messages } from '../../messages';
import ProductTypeAheadInput from '../../../../../../../components/product-type-ahead-input/product-type-ahead-input';
import style from '../../style.module.less';

const EditProductNameColumnId = 'edit-name';
const NamePropertyName = 'name';

const editProductNameDataBuilder = (item) => {
  return {
    id: item.id,
    name: item.name,
    readonly: item.readonly,
    error: item.error,
    shouldShowError: item.shouldShowError,
  };
};

const EditProductName = ({ value: item, onColumnBlur, disabled = false }) => {
  const context = useContext(ProductDetailsContext);
  const parentProduct = {
    name: context?.productName,
    sku: context?.productSku,
  };
  const [name, setName] = useControlledState(item.name);

  const shouldShowErrorRef = useRef(false);
  const onBlur = useCallback(
    (event) => {
      shouldShowErrorRef.current = true;
      onColumnBlur(item.id, {
        [NamePropertyName]: event?.target?.value,
      });
    },
    [onColumnBlur, item.id],
  );

  const onResultSelected = useCallback(
    (product) => {
      onColumnBlur(item.id, {
        name: product.name,
        sku: product.sku,
        productId: product.productId,
        productThumbnailUrl: product.thumbnailUrl,
      });
    },
    [onColumnBlur, item.id],
  );

  const onChange = useCallback(
    (value) => {
      setName(value);
    },
    [setName],
  );

  const error = useMemo(() => {
    return item.error ? <FormattedMessage {...item.error.message} /> : undefined;
  }, [item]);

  if (item.readonly) {
    return <div aria-labelledby={EditProductNameColumnId}>{name}</div>;
  }

  return (
    <ProductTypeAheadInput
      ariaLabelledBy={EditProductNameColumnId}
      className={style.input}
      disabled={disabled}
      errorMessage={shouldShowErrorRef.current || item.shouldShowError ? error : undefined}
      excludeBundles
      onBlur={onBlur}
      onChange={onChange}
      onResultSelected={onResultSelected}
      parentProduct={parentProduct}
      shouldShowAboveInput
      value={name}
    />
  );
};

EditProductName.propTypes = {
  value: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string,
    readonly: PropTypes.shape({
      code: PropTypes.string,
    }),
    error: PropTypes.shape({
      message: PropTypes.object,
    }),
    shouldShowError: PropTypes.bool,
  }).isRequired,
  onColumnBlur: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

const createEditProductNameColumnDefinition = createColumnDefinitionBuilder({
  header: <FormattedMessage {...messages.productBundleComponentName} />,
  accessor: EditProductNameColumnId,
  cell: EditProductName,
  width: 1.5,
  className: style.column,
});

export { EditProductNameColumnId, editProductNameDataBuilder, createEditProductNameColumnDefinition };
